import { PROBLEM_TYPES } from '@rheora/enrollment-contract';

/**
 * Classifying WHY `POST /bff/quote/draft` refused, so the estimate screen can
 * offer a route forward instead of one dead end (QA-2026-08-06).
 *
 * ── THE DEFECT THIS CLOSES ─────────────────────────────────────────────────
 * Three materially different upstream conditions reached the shopper as the same
 * alert — "We couldn't prepare your agreement · Something went wrong setting up
 * your quote. Please try again in a moment." — with nothing to click:
 *
 *   · the meter already has an enrollment in progress  (409 esiid-already-enrolled)
 *   · the meter is not switchable right now            (409 esiid-not-active)
 *   · the ESI ID cannot belong to that utility         (422 esiid-tdsp-mismatch)
 *
 * Retrying resolves none of them, and the remedy differs for each: contact
 * support, choose another meter, or correct the ESI ID. The BFF has said which is
 * which since QA-2026-08-06; the storefront was not listening.
 *
 * ── WHY BRANCH ON `type` AND NOT ON THE STATUS ─────────────────────────────
 * Two of the three are 409s, so the status cannot tell them apart. `PROBLEM_TYPES`
 * is the SHARED contract both sides import — the storefront cannot drift from the
 * BFF's vocabulary without a compile error, which a hand-copied string literal
 * would happily allow.
 *
 * ── WHY THE COPY IS NOT READ OFF THE WIRE ──────────────────────────────────
 * The problem body's `detail` is English prose written for an operator-readable
 * envelope. Rendering it would put an untranslated, unversioned server string in
 * front of the customer and bypass `t()` entirely. So the wire carries the
 * DISCRIMINATOR and the storefront owns the words.
 */

/**
 * What actually went wrong, in terms the estimate screen can act on.
 *
 * `unknown` is deliberately retained and deliberately last: an unrecognised
 * failure — a genuine outage, a network drop, a problem type this build predates
 * — must keep the existing generic alert rather than be forced into whichever
 * branch reads closest. A wrong-but-specific explanation is worse than an honest
 * vague one.
 */
export type QuoteDraftFailure =
  | 'already-enrolled'
  | 'meter-not-active'
  | 'esiid-tdsp-mismatch'
  // ── E-SF16, the offer path. Same defect class, three more conditions. ─────
  // On an offer entry this step REPOINTS the agent's quote (`select-plan`) rather
  // than creating one, so it can fail in ways the self-serve path cannot. Each
  // has a different remedy, and none of them is "try again":
  //   plan-not-in-offer  → choose a plan from the offer (self-service, in-session)
  //   offer-unavailable  → nothing in-session helps; only the agent can re-send
  //   price-changed      → confirm the live price, or pick another plan
  | 'plan-not-in-offer'
  | 'offer-unavailable'
  | 'price-changed'
  | 'unknown';

/**
 * Classify a quote-draft refusal from its problem `type`.
 *
 * Takes the bare URN (read by `problemTypeOf` in the api layer) rather than the
 * error object, so this module stays pure and has no dependency on the network
 * seam — the classification is a domain rule, not a transport concern.
 *
 * @param problemType the BFF's RFC7807 `type`, or `undefined` when the failure
 *   carried none (a transport drop, a thrown non-`BffError`). Unrecognised and
 *   absent both classify as `unknown`, which keeps the existing generic alert.
 */
export function classifyQuoteDraftFailure(problemType: string | undefined): QuoteDraftFailure {
  switch (problemType) {
    case PROBLEM_TYPES.esiidAlreadyEnrolled:
      return 'already-enrolled';
    case PROBLEM_TYPES.esiidNotActive:
      return 'meter-not-active';
    case PROBLEM_TYPES.esiidTdspMismatch:
      return 'esiid-tdsp-mismatch';
    // E-SF16 — the offer path's three refusals.
    case PROBLEM_TYPES.planNotInOffer:
      return 'plan-not-in-offer';
    case PROBLEM_TYPES.offerNoLongerAvailable:
      return 'offer-unavailable';
    case PROBLEM_TYPES.offerPriceChanged:
      return 'price-changed';
    default:
      return 'unknown';
  }
}

/** The i18n keys the alert renders for a classified failure. */
export interface QuoteDraftFailureCopy {
  readonly titleKey: string;
  readonly bodyKey: string;
  /**
   * True for the catch-all copy — the branch that has no route forward attached
   * (E-SF19 P1). Only this one may be replaced by the server's own `detail`; a
   * recognised refusal already says something more useful AND offers an action.
   */
  readonly isGeneric?: boolean;
}

/**
 * The copy keys for a failure. Pure key selection — no strings, no formatting, so
 * the component stays render-only and every word still goes through `t()`.
 *
 * The `unknown` branch keeps the ORIGINAL generic keys, unchanged. That is the
 * point: the fix adds specific answers where we have one, and does not quietly
 * relabel every remaining failure as something we understand.
 */
export function quoteDraftFailureCopy(failure: QuoteDraftFailure): QuoteDraftFailureCopy {
  switch (failure) {
    case 'already-enrolled':
      return {
        titleKey: 'shop.estimate.continue.error.alreadyEnrolled.title',
        bodyKey: 'shop.estimate.continue.error.alreadyEnrolled.body',
      };
    case 'meter-not-active':
      return {
        titleKey: 'shop.estimate.continue.error.meterNotActive.title',
        bodyKey: 'shop.estimate.continue.error.meterNotActive.body',
      };
    case 'esiid-tdsp-mismatch':
      return {
        titleKey: 'shop.estimate.continue.error.esiidMismatch.title',
        bodyKey: 'shop.estimate.continue.error.esiidMismatch.body',
      };
    case 'plan-not-in-offer':
      return {
        titleKey: 'shop.estimate.continue.error.planNotInOffer.title',
        bodyKey: 'shop.estimate.continue.error.planNotInOffer.body',
      };
    case 'offer-unavailable':
      return {
        titleKey: 'shop.estimate.continue.error.offerUnavailable.title',
        bodyKey: 'shop.estimate.continue.error.offerUnavailable.body',
      };
    case 'price-changed':
      return {
        titleKey: 'shop.estimate.continue.error.priceChanged.title',
        bodyKey: 'shop.estimate.continue.error.priceChanged.body',
      };
    case 'unknown':
      return {
        titleKey: 'shop.estimate.continue.error.title',
        bodyKey: 'shop.estimate.continue.error.body',
        // The catch-all: no route forward, so this is the ONE branch the BFF's own
        // customer-safe `detail` may speak over (E-SF19 P1).
        isGeneric: true,
      };
  }
}

/**
 * True when the shopper should be offered a way back to the meter chooser.
 *
 * ⚠️ NOT offered for `already-enrolled`, and that is a security decision rather
 * than a UX one. Restarting cannot help — the meter is enrolled and will refuse
 * again — but more importantly the BFF deliberately withholds the `openQuoteId`
 * (`response-guard.ts`), because a resumable link keyed off a meter is an ESIID
 * oracle: anyone who can guess a meter could probe whether it is enrolled. So
 * that branch routes to a human, and only to a human.
 */
export function offersMeterRestart(failure: QuoteDraftFailure): boolean {
  return failure === 'meter-not-active' || failure === 'esiid-tdsp-mismatch';
}

/**
 * The i18n key for the restart control's label, or `null` when the failure
 * offers no restart.
 *
 * The two restart branches say DIFFERENT things — "try a different meter" versus
 * "re-enter your ESI ID" — even though both land on the same chooser, because
 * the shopper needs to know which of the two they got wrong.
 */
/**
 * True when the customer should be offered a way back to the plans shelf
 * (E-SF16).
 *
 * ⚠️ ONLY for `plan-not-in-offer`, and that is the whole point of keeping it
 * separate from `offer-unavailable`. "Choose a different plan" is a real remedy
 * when the OFFER is fine and the PLAN is wrong; it is a false promise when the
 * offer itself has expired, because no plan on that shelf will work either.
 * Collapsing the two conditions into one failure would put that false promise in
 * front of every customer whose link aged out.
 */
export function offersPlanShelfReturn(failure: QuoteDraftFailure): boolean {
  return failure === 'plan-not-in-offer';
}

/**
 * True when the customer should be offered "continue at the current price"
 * (E-SF16).
 *
 * ── WHAT THE CUSTOMER IS ACTUALLY CONFIRMING ───────────────────────────────
 * The price ON THIS SCREEN. The storefront prices every card from the LIVE Pricing
 * catalogue, so the figure the customer has been reading all the way down the
 * funnel is the current one; what moved is the price the AGENT quoted, which this
 * app has never shown and (per ADR-0010 D1.2) must never show. So the affordance
 * is an honest re-agreement to a number already in front of them, not an
 * acknowledgement of a change they have to be told about first.
 *
 * ⚠️ THE CONFIRMATION CARRIES NO TOKEN. The upstream's `repricedSnapshotId` never
 * reaches the browser; the retry sends `confirmPriceChange: true` and the BFF
 * supplies the token it stashed on the funnel session. A client therefore cannot
 * confirm a price change the server did not just report to it.
 */
export function offersPriceConfirmation(failure: QuoteDraftFailure): boolean {
  return failure === 'price-changed';
}

export function meterRestartLabelKey(failure: QuoteDraftFailure): string | null {
  switch (failure) {
    case 'meter-not-active':
      return 'shop.estimate.continue.error.meterNotActive.action';
    case 'esiid-tdsp-mismatch':
      return 'shop.estimate.continue.error.esiidMismatch.action';
    // The offer-path refusals route forward through their OWN affordances
    // (`offersPlanShelfReturn` / `offersPriceConfirmation`), never through the
    // meter chooser: changing meter cannot help when the offer names a premise.
    case 'already-enrolled':
    case 'plan-not-in-offer':
    case 'offer-unavailable':
    case 'price-changed':
    case 'unknown':
      return null;
  }
}
